/**
 * My Vehicle page — v2. Customer-facing, no login, reached from drvlgc.co/s/<id>
 * short URL in every reminder text. Mobile-first. Fully dealer-branded from
 * settings. Ships with:
 *   1. Tap-the-car SVG diagram — gold pulsing hotspots per open item
 *   2. Video-first hotspot cards — item video ("Your vehicle") or dealer
 *      category explainer ("Why this matters"), muted until tap, captions
 *   3. Semicircle Health gauge — animated needle, colored zones
 *   4. Live offer countdown — days/hours/minutes to expiry
 *   5. One-tap booking — 6 dealer-open slots as buttons; reschedule + cancel
 */

import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  CheckCircle2, Phone, Loader2, X, Clock, ShieldCheck,
  CalendarCheck, Video, PlayCircle, AlertTriangle,
} from "lucide-react";

/** Hotspot positions on the SVG viewBox (0-400 × 0-160) — tuned for a
 *  clean side-view profile. Each maps to a service `area` value. */
const HOTSPOTS = {
  front_brakes: { x: 305, y: 118, label: "Front Brakes" },
  rear_brakes:  { x:  95, y: 118, label: "Rear Brakes" },
  tires:        { x:  60, y: 130, label: "Tires" },
  battery:      { x: 335, y:  60, label: "Battery" },
  alignment:    { x: 200, y: 145, label: "Alignment" },
  suspension:   { x: 260, y: 100, label: "Suspension" },
  ac:           { x: 220, y:  55, label: "A/C" },
  filters:      { x: 175, y:  50, label: "Filters" },
  fluids:       { x: 145, y:  50, label: "Fluids" },
  wipers:       { x: 250, y:  40, label: "Wipers" },
  other:        { x: 200, y:  30, label: "Other" },
};

const AREA_TO_CATEGORY = {
  front_brakes: "brakes", rear_brakes: "brakes", tires: "tires",
  battery: "battery", alignment: "alignment", suspension: "suspension",
  ac: "ac", filters: "filters", fluids: "fluids", wipers: "wipers",
};

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    .format(Number(n || 0));

function formatSlot(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

/** Live countdown to `deadline` (ISO). Returns {expired, d, h, m}. */
function useCountdown(deadline) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    const t = new Date(deadline).getTime() - now;
    if (isNaN(t) || t <= 0) return { expired: true, d: 0, h: 0, m: 0 };
    const d = Math.floor(t / 86400000);
    const h = Math.floor((t % 86400000) / 3600000);
    const m = Math.floor((t % 3600000) / 60000);
    return { expired: false, d, h, m };
  }, [deadline, now]);
}

/** Semicircle SVG gauge, needle animates on score change. */
function HealthGauge({ score = 100, primary, testid }) {
  const color = score >= 80 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";
  const label = score >= 80 ? "Good" : score >= 50 ? "Needs attention" : "Critical";
  // Needle rotates from -90° (left, score=0) to +90° (right, score=100)
  const angle = -90 + (score / 100) * 180;
  return (
    <div className="text-center" data-testid={testid}>
      <svg viewBox="0 0 200 110" className="w-full max-w-[280px] mx-auto">
        <defs>
          <linearGradient id="gaugeGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        {/* Arc track */}
        <path d="M 15 100 A 85 85 0 0 1 185 100" fill="none" stroke="url(#gaugeGrad)" strokeWidth="14" strokeLinecap="round" />
        {/* Needle */}
        <g style={{ transform: `rotate(${angle}deg)`, transformOrigin: "100px 100px", transition: "transform 900ms cubic-bezier(.34,1.56,.64,1)" }}>
          <line x1="100" y1="100" x2="100" y2="30" stroke={primary} strokeWidth="4" strokeLinecap="round" />
          <circle cx="100" cy="100" r="8" fill={primary} />
        </g>
        {/* Score text */}
        <text x="100" y="90" textAnchor="middle" fontSize="26" fontWeight="900" fill={primary}>{score}</text>
      </svg>
      <div className="text-xs uppercase tracking-widest font-mono font-semibold mt-1" style={{ color }}>
        {label}
      </div>
    </div>
  );
}

/** Side-view car SVG with tappable hotspots for each open item's area. */
function CarDiagram({ items, secondary, onTap, testid }) {
  const openByArea = {};
  items.forEach((it, idx) => {
    if (it.closed || it.scheduled) return;
    const a = HOTSPOTS[it.area] ? it.area : "other";
    (openByArea[a] ||= []).push({ ...it, _idx: idx });
  });
  const doneByArea = {};
  items.forEach((it) => {
    if (!(it.closed || it.scheduled)) return;
    const a = HOTSPOTS[it.area] ? it.area : "other";
    (doneByArea[a] ||= []).push(it);
  });

  return (
    <div className="relative w-full" data-testid={testid}>
      <svg viewBox="0 0 400 170" className="w-full h-auto">
        {/* Simple side-view car — body, cabin, wheels */}
        <path
          d="M 30 130 L 70 130 A 22 22 0 0 1 114 130 L 285 130 A 22 22 0 0 1 329 130 L 370 130 L 370 108 L 360 88 L 305 60 L 235 50 L 175 50 L 130 70 L 90 90 L 30 100 Z"
          fill="#eef1f6" stroke="#c7cdd6" strokeWidth="1.5"
        />
        {/* Windows */}
        <path d="M 180 55 L 230 55 L 280 78 L 180 78 Z" fill="#c9d2df" opacity="0.9" />
        <path d="M 132 70 L 175 55 L 175 78 L 132 78 Z" fill="#c9d2df" opacity="0.9" />
        {/* Wheels */}
        <circle cx="92" cy="132" r="22" fill="#1a1d24" />
        <circle cx="92" cy="132" r="10" fill="#4a5060" />
        <circle cx="307" cy="132" r="22" fill="#1a1d24" />
        <circle cx="307" cy="132" r="10" fill="#4a5060" />
        {/* Ground line */}
        <line x1="0" y1="158" x2="400" y2="158" stroke="#d3d8e0" strokeDasharray="4 6" />
      </svg>

      {/* Hotspot layer — absolute-positioned buttons over the svg */}
      <div className="absolute inset-0">
        {Object.entries(openByArea).map(([area, its]) => {
          const spot = HOTSPOTS[area];
          if (!spot) return null;
          const first = its[0];
          return (
            <button
              key={area}
              onClick={() => onTap(first._idx)}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${(spot.x / 400) * 100}%`, top: `${(spot.y / 170) * 100}%` }}
              data-testid={`hotspot-${area}`}
              aria-label={`${spot.label} — ${its.length} item${its.length > 1 ? "s" : ""}`}
            >
              <span
                className="block w-6 h-6 rounded-full animate-ping absolute inset-0"
                style={{ backgroundColor: secondary, opacity: 0.35 }}
              />
              <span
                className="relative block w-6 h-6 rounded-full ring-2 ring-white shadow-lg flex items-center justify-center text-white font-bold text-xs"
                style={{ backgroundColor: secondary }}
              >
                {its.length > 1 ? its.length : "!"}
              </span>
            </button>
          );
        })}
        {Object.entries(doneByArea).map(([area]) => {
          const spot = HOTSPOTS[area];
          if (!spot) return null;
          return (
            <div
              key={`done-${area}`}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${(spot.x / 400) * 100}%`, top: `${(spot.y / 170) * 100}%` }}
              data-testid={`hotspot-done-${area}`}
            >
              <span className="block w-6 h-6 rounded-full bg-emerald-500 ring-2 ring-white shadow-lg flex items-center justify-center">
                <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyVehiclePage() {
  const { declineId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeItemIdx, setActiveItemIdx] = useState(null);
  const [booking, setBooking] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const trackedTaps = useRef(new Set());
  const trackedVideos = useRef(new Set());

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/public/decline/${declineId}`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e.response?.status === 404 ? "not-found" : "load-failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [declineId]);

  const brand = data?.dealership || {};
  const primary = brand.brand_primary_color || "#1b2a4a";
  const secondary = brand.brand_secondary_color || "#b8912f";
  const dealerName = brand.name || "Your Dealership";

  const items = data?.service_items || [];
  const activeItem = activeItemIdx !== null ? items[activeItemIdx] : null;
  const isScheduled = data?.status === "scheduled";
  const offerPct = data?.current_offer_percent || 0;
  const countdown = useCountdown(data?.offer_expires_at);
  const offerExpired = countdown.expired;

  // Fire hotspot_tap event once per session per item
  const openItem = (idx) => {
    setActiveItemIdx(idx);
    const key = `tap-${idx}`;
    if (!trackedTaps.current.has(key)) {
      trackedTaps.current.add(key);
      axios.post(`${API}/public/decline/${declineId}/track`, { event: "hotspot_tap", item_index: idx }).catch(() => {});
    }
  };

  const onVideoPlay = (idx) => {
    const key = `vid-${idx}`;
    if (!trackedVideos.current.has(key)) {
      trackedVideos.current.add(key);
      axios.post(`${API}/public/decline/${declineId}/track`, { event: "video_view", item_index: idx }).catch(() => {});
    }
  };

  const bookSlot = async (isoTime) => {
    setBooking(true);
    try {
      await axios.post(`${API}/public/decline/${declineId}/onetap-schedule`, {
        scheduled_for: isoTime,
        item_indexes: null, // all open items
      });
      toast.success("Booked! You'll get a confirmation text shortly.");
      load();
    } catch {
      toast.error("Couldn't book that slot — try another or call us.");
    } finally { setBooking(false); }
  };

  const doReschedule = async (isoTime) => {
    setBooking(true);
    try {
      await axios.post(`${API}/public/decline/${declineId}/reschedule`, { scheduled_for: isoTime });
      toast.success("Rescheduled!");
      setRescheduleOpen(false);
      load();
    } catch { toast.error("Reschedule failed."); }
    finally { setBooking(false); }
  };

  const doCancel = async () => {
    setBooking(true);
    try {
      await axios.post(`${API}/public/decline/${declineId}/cancel`);
      toast.success("Cancelled. You can rebook anytime.");
      load();
    } catch { toast.error("Cancel failed."); }
    finally { setBooking(false); }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-neutral-50"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>;
  }
  if (error === "not-found" || !data) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-neutral-50">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
          <h1 className="text-xl font-bold mb-2">Link not found</h1>
          <p className="text-sm text-neutral-600">Call your dealership for help.</p>
        </div>
      </div>
    );
  }
  if (data.expired) {
    return (
      <div className="min-h-screen grid place-items-center p-6" style={{ backgroundColor: "#f8f9fb" }}>
        <div className="max-w-sm text-center">
          <Clock className="w-10 h-10 mx-auto mb-3" style={{ color: secondary }} />
          <h1 className="text-xl font-bold mb-2" style={{ color: primary }}>This link has expired</h1>
          <p className="text-sm text-neutral-600 mb-4">Call {dealerName} and we'll still take care of you.</p>
          {brand.phone && (
            <a href={`tel:${brand.phone}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-md" style={{ backgroundColor: primary }} data-testid="expired-call-btn">
              <Phone className="w-4 h-4" /> Call {brand.phone}
            </a>
          )}
        </div>
      </div>
    );
  }

  const slotsToShow = showAllOptions ? (data.open_slots || []) : (data.open_slots || []).slice(0, 6);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f7f7f9" }} data-testid="my-vehicle-page">
      {/* Header */}
      <header className="sticky top-0 z-30 shadow-sm" style={{ backgroundColor: primary }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={dealerName} className="h-10 w-auto object-contain rounded bg-white/10 p-1" data-testid="dealer-logo" />
          ) : (
            <div className="h-10 w-10 rounded-full grid place-items-center" style={{ backgroundColor: secondary }}>
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-white font-bold text-base truncate leading-tight" data-testid="dealer-name">{dealerName}</div>
            {brand.phone && (
              <a href={`tel:${brand.phone}`} className="text-white/80 text-xs hover:text-white flex items-center gap-1" data-testid="dealer-phone">
                <Phone className="w-3 h-3" /> {brand.phone}
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 pb-32">
        {/* Vehicle hero */}
        <section className="mb-4" data-testid="vehicle-hero">
          <div className="text-xs uppercase tracking-widest font-mono" style={{ color: secondary }}>
            {data.customer_first_name ? `Hi ${data.customer_first_name}` : "Your vehicle"}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1 leading-tight" style={{ color: primary }}>
            {data.vehicle}
          </h1>
          {data.mileage && <div className="text-sm text-neutral-500 mt-1">{Number(data.mileage).toLocaleString()} miles at last visit</div>}
        </section>

        {/* Car diagram + hotspots */}
        <section className="bg-white rounded-2xl border shadow-sm p-4 mb-4" style={{ borderColor: `${primary}22` }} data-testid="car-diagram-section">
          <div className="text-[0.7rem] uppercase tracking-widest font-mono text-neutral-500 mb-2">
            Tap any glowing spot
          </div>
          <CarDiagram items={items} secondary={secondary} onTap={openItem} testid="car-svg" />
          {/* Health gauge */}
          <div className="mt-3 pt-3 border-t" style={{ borderColor: `${primary}15` }}>
            <HealthGauge score={data.health_score ?? 100} primary={primary} testid="health-gauge" />
            <div className="text-center text-xs text-neutral-500 mt-1">
              {items.filter(i => !i.scheduled && !i.closed).length} items need attention
            </div>
          </div>
        </section>

        {/* Countdown + savings */}
        {!isScheduled && offerPct > 0 && (
          <section
            className="rounded-2xl p-4 mb-4 text-white shadow-md relative overflow-hidden"
            style={{ backgroundColor: primary }}
            data-testid="countdown-card"
          >
            <div className="absolute right-3 top-3 opacity-15"><Clock className="w-16 h-16" /></div>
            <div className="text-[0.7rem] uppercase tracking-widest font-semibold" style={{ color: secondary }}>Your offer ends in</div>
            {offerExpired ? (
              <div className="text-2xl font-black mt-1">Offer expired — call us</div>
            ) : (
              <div className="text-3xl font-black mt-1 tabular-nums" data-testid="countdown-value">
                {countdown.d}d {countdown.h}h {countdown.m}m
              </div>
            )}
            <div className="mt-3 flex items-baseline gap-3">
              <div>
                <div className="text-[0.6rem] uppercase tracking-widest text-white/60">Today's discount</div>
                <div className="text-2xl font-black" data-testid="offer-percent">{offerPct}% OFF</div>
              </div>
              <div className="text-white/40 text-2xl">·</div>
              <div>
                <div className="text-[0.6rem] uppercase tracking-widest text-white/60">You save</div>
                <div className="text-2xl font-black tabular-nums" data-testid="dollar-savings">{money(data.dollar_savings)}</div>
              </div>
            </div>
            <div className="text-xs text-white/70 mt-2">Code <b className="text-white">{data.coupon_code}</b></div>
          </section>
        )}

        {/* Already scheduled banner */}
        {isScheduled && (
          <section className="rounded-2xl p-4 mb-4 bg-emerald-50 border border-emerald-200" data-testid="scheduled-banner">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-emerald-900">You're on the schedule</div>
                <div className="text-sm text-emerald-800 mt-0.5">Your advisor will confirm shortly.</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setRescheduleOpen(true)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-800" data-testid="reschedule-btn">
                    Reschedule
                  </button>
                  <button onClick={doCancel} disabled={booking} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-neutral-300 text-neutral-600" data-testid="cancel-btn">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* One-tap booking */}
        {!isScheduled && (data.open_slots || []).length > 0 && (
          <section className="mb-4" data-testid="onetap-section">
            <div className="text-[0.7rem] uppercase tracking-widest font-mono text-neutral-500 mb-2">
              Book in one tap
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {slotsToShow.map((iso) => (
                <button
                  key={iso}
                  onClick={() => bookSlot(iso)}
                  disabled={booking}
                  className="rounded-xl bg-white border p-3 text-sm font-semibold shadow-sm active:opacity-70 disabled:opacity-50"
                  style={{ color: primary, borderColor: `${primary}44` }}
                  data-testid={`slot-${iso.replace(/[:.]/g, "-")}`}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <CalendarCheck className="w-4 h-4" style={{ color: secondary }} />
                    {formatSlot(iso)}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Dealer footer */}
        <footer className="text-center text-xs text-neutral-500 mt-8" data-testid="dealer-footer">
          <div className="font-semibold text-neutral-700">{dealerName}</div>
          {brand.hours && <div className="mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> {brand.hours}</div>}
          {brand.phone && <div className="mt-1"><a href={`tel:${brand.phone}`} className="hover:underline" style={{ color: primary }}>{brand.phone}</a></div>}
          <div className="mt-3 text-[0.6rem] uppercase tracking-widest text-neutral-400">Link valid {data.ttl_days} days · Reply STOP to opt out</div>
        </footer>
      </main>

      {/* Hotspot modal — video card */}
      {activeItem && (
        <HotspotModal
          item={activeItem}
          idx={activeItemIdx}
          data={data}
          primary={primary}
          secondary={secondary}
          onClose={() => setActiveItemIdx(null)}
          onSchedule={(iso) => bookSlot(iso).then(() => setActiveItemIdx(null))}
          onVideoPlay={onVideoPlay}
          slots={(data.open_slots || []).slice(0, 3)}
        />
      )}

      {/* Reschedule modal */}
      {rescheduleOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setRescheduleOpen(false)} data-testid="reschedule-modal">
          <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold" style={{ color: primary }}>Pick a new time</h2>
              <button onClick={() => setRescheduleOpen(false)}><X className="w-5 h-5 text-neutral-500" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(data.open_slots || []).slice(0, 6).map((iso) => (
                <button key={iso} onClick={() => doReschedule(iso)} disabled={booking} className="rounded-xl border p-3 text-sm font-semibold" style={{ color: primary, borderColor: `${primary}44` }}>
                  {formatSlot(iso)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Toaster position="top-center" />
    </div>
  );
}

function HotspotModal({ item, idx, data, primary, secondary, onClose, onSchedule, onVideoPlay, slots }) {
  const category = AREA_TO_CATEGORY[item.area] || null;
  const libraryVideo = (data.explainer_videos || {})[category] || "";
  const useOwnVideo = !!item.video_url;
  const videoUrl = useOwnVideo ? item.video_url : libraryVideo;
  const videoLabel = useOwnVideo ? "Your vehicle" : (videoUrl ? "Why this matters" : null);
  const offerPct = data.current_offer_percent || 0;
  const priceAfter = offerPct > 0 ? item.price * (1 - offerPct / 100) : item.price;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose} data-testid="hotspot-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Video block */}
        {videoUrl ? (
          <div className="relative bg-black" style={{ aspectRatio: "16/9" }}>
            <video
              src={videoUrl}
              controls
              playsInline
              muted
              onPlay={() => onVideoPlay?.(idx)}
              className="w-full h-full object-cover"
              data-testid="hotspot-video"
            >
              <track kind="captions" srcLang="en" label="English" />
            </video>
            <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[0.6rem] font-mono uppercase tracking-widest text-white" style={{ backgroundColor: secondary }}>
              {videoLabel}
            </div>
          </div>
        ) : item.photo_url ? (
          <img src={item.photo_url} alt={item.name} className="w-full object-cover" style={{ aspectRatio: "16/9" }} />
        ) : (
          <div className="h-24 flex items-center justify-center" style={{ backgroundColor: `${primary}0a` }}>
            <Video className="w-10 h-10" style={{ color: `${primary}55` }} />
          </div>
        )}

        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <div className="text-[0.65rem] uppercase tracking-widest font-mono" style={{ color: secondary }}>
                {HOTSPOTS[item.area]?.label || "Service"} · {item.severity?.toUpperCase()}
              </div>
              <h2 className="text-xl font-bold leading-tight" style={{ color: primary }} data-testid="hotspot-name">{item.name}</h2>
            </div>
            <button onClick={onClose} aria-label="Close" data-testid="hotspot-modal-close">
              <X className="w-5 h-5 text-neutral-400" />
            </button>
          </div>
          {(item.measurement || item.notes) && (
            <div className="mt-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200 text-sm text-neutral-700">
              {item.measurement && <div className="font-semibold" data-testid="hotspot-measurement">{item.measurement}</div>}
              {item.notes && <div className="mt-1 text-neutral-600">{item.notes}</div>}
            </div>
          )}
          <div className="mt-4 flex items-baseline justify-between">
            <div>
              <div className="text-[0.6rem] uppercase tracking-widest font-mono text-neutral-500">Estimate</div>
              <div className="text-2xl font-bold tabular-nums" style={{ color: primary }}>{money(item.price)}</div>
            </div>
            {offerPct > 0 && (
              <div className="text-right">
                <div className="text-[0.6rem] uppercase tracking-widest font-mono" style={{ color: secondary }}>With {offerPct}% off</div>
                <div className="text-2xl font-black tabular-nums" style={{ color: primary }}>{money(priceAfter)}</div>
              </div>
            )}
          </div>

          {slots.length > 0 && (
            <>
              <div className="mt-4 text-[0.65rem] uppercase tracking-widest font-mono text-neutral-500">Schedule this in one tap</div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {slots.map((iso) => (
                  <button
                    key={iso}
                    onClick={() => onSchedule(iso)}
                    className="rounded-lg py-2 text-xs font-semibold text-white shadow-sm active:opacity-80"
                    style={{ backgroundColor: primary }}
                    data-testid={`hotspot-slot-${iso.replace(/[:.]/g, "-")}`}
                  >
                    {formatSlot(iso)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
